import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, MenuItem, Select, FormControl, InputLabel, IconButton
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LayersIcon from '@mui/icons-material/Layers';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';

const INDIGO    = '#283593';
const INDIGO_LT = '#e8eaf6';

const HDR_SX = {
  fontWeight: 700,
  color: '#fff',
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  background: `linear-gradient(135deg, ${INDIGO} 0%, #1a237e 100%)`,
  borderRight: '1px solid rgba(255,255,255,0.2)',
  py: 1.2,
  px: 0.8,
  whiteSpace: 'nowrap',
  textAlign: 'center',
};
const CELL_SX = { py: 0.9, px: 0.8, fontSize: '0.77rem', textAlign: 'center' };

function fmtNum(v, d = 2) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}
function fmtDate(v) {
  if (!v) return '—';
  return String(v).substring(0, 10);
}
function isGrandTotal(row) {
  const key = row.MACHINE_NAME ?? row.MACHINE ?? row.SIZE ?? '';
  return String(key).trim().toUpperCase().startsWith('GRAND TOTAL');
}

// ─── Detail table (RS[0]) ─────────────────────────────────────────────────────
const DETAIL_COLS = [
  { label: '#',           key: '__idx',        align: 'center' },
  { label: 'SLIP NO',     key: 'SLIPNO',       align: 'center' },
  { label: 'COMPANY',     key: 'COMPANY',      align: 'center' },
  { label: 'SIZE',        key: 'SIZE',         align: 'center' },
  { label: 'LENGTH',      key: 'LENGTH',       align: 'center', fmt: v => fmtNum(v, 2) },
  { label: 'WIDTH',       key: 'WIDTH',        align: 'center', fmt: v => fmtNum(v, 2) },
  { label: 'THICKNESS',   key: 'THICKNESS',    align: 'center', fmt: v => fmtNum(v, 2) },
  { label: 'PCS',         key: 'PCS',          align: 'center', fmt: v => fmtNum(v, 0) },
  { label: 'CBM',         key: 'CBM',          align: 'center', fmt: v => fmtNum(v, 3) },
  { label: 'QUALITY',     key: 'QUALITY',      align: 'center' },
  { label: 'CORE NAME',   key: 'CORENAME',     align: 'center' },
  { label: 'MACHINE',     key: 'MACHINE_NAME', align: 'center' },
  { label: 'OPERATOR',    key: 'OPERATOR',     align: 'center' },
  { label: 'SHIFT',       key: 'WORK_SHIFT',   align: 'center' },
  { label: 'CERTIFICATE', key: 'CERTIFICATE',  align: 'center' },
  { label: 'PROD. DATE',  key: 'PRODUCTION_DATE', align: 'center', fmt: fmtDate },
];

function DetailTable({ rows }) {
  if (!rows?.length) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;
  return (
    <TableContainer sx={{ maxHeight: 560, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 2000 }}>
        <TableHead>
          <TableRow>
            {DETAIL_COLS.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{
              backgroundColor: i % 2 === 0 ? '#fff' : INDIGO_LT,
              '&:hover': { backgroundColor: '#c5cae9' },
            }}>
              {DETAIL_COLS.map(col => {
                const val = col.key === '__idx'
                  ? i + 1
                  : col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '—');
                return (
                  <TableCell key={col.label} sx={{ ...CELL_SX, textAlign: col.align }}>
                    {val}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Summary table (RS[1] day, RS[2] night, RS[3] combined, RS[4] month) ─────
const SUMMARY_COLS = [
  { label: 'MACHINE',  key: 'MACHINE_NAME', align: 'center' },
  { label: 'PCS',      key: 'PCS',          align: 'center', fmt: v => fmtNum(v, 0) },
  { label: 'CBM',      key: 'CBM',          align: 'center', fmt: v => fmtNum(v, 3) },
];

function SummaryTable({ rows }) {
  if (!rows?.length) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 480 }}>
        <TableHead>
          <TableRow>
            {SUMMARY_COLS.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const grand = isGrandTotal(row);
            return (
              <TableRow key={i} sx={{
                backgroundColor: grand ? '#e8eaf6' : (i % 2 === 0 ? '#fff' : INDIGO_LT),
                '&:hover': { filter: 'brightness(0.97)' },
              }}>
                {SUMMARY_COLS.map(col => {
                  const val = col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '—');
                  return (
                    <TableCell key={col.label} sx={{
                      ...CELL_SX,
                      textAlign: col.align,
                      fontWeight: grand ? 700 : 500,
                      color: grand ? INDIGO : 'inherit',
                    }}>
                      {val}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const TAB_LABELS = ['DETAIL', 'DAY SUMMARY', 'NIGHT SUMMARY', 'COMBINED SUMMARY', 'MONTH SUMMARY'];

// ─── Main component ───────────────────────────────────────────────────────────
const DailyCoreComposerReportView = ({ onBackClick, user }) => {
  const [date,    setDate]    = useState(null);
  const [shift,   setShift]   = useState('DAY');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [data,    setData]    = useState(null);
  const [tab,     setTab]     = useState(0);

  const fetchData = async () => {
    if (!date) { setError('Please select a date'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/api/core-composer/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: date.format('YYYY-MM-DD'), shift }),
      });
      if (!res.ok) throw new Error('Server error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.success) throw new Error(apiData.message || 'API error');
      if (!apiData.data || apiData.data.every(rs => rs.length === 0)) {
        setError('No data found for the selected date and shift.');
        return;
      }
      setData(apiData.data);
      setTab(0);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    TAB_LABELS.forEach((label, i) => {
      if (data[i]?.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[i]), label.substring(0, 31));
      }
    });
    XLSX.writeFile(wb, `DailyCoreComposer_${shift}_${date.format('YYYY-MM-DD')}.xlsx`);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>

      {/* ── Header card ─────────────────────────────────────────────────────── */}
      <Card sx={{
        mb: 3, borderRadius: 3,
        boxShadow: '0 4px 12px rgba(40,53,147,0.12)',
        border: '1px solid #c5cae9',
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: INDIGO }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{
              background: `linear-gradient(135deg, ${INDIGO} 0%, #1a237e 100%)`,
              borderRadius: 2, p: 1.5, mr: 2,
            }}>
              <LayersIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: INDIGO }}>
                Daily Core Composer Report — By Machine
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Core composer production — by date and shift
              </Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
              <DatePicker
                label="Date"
                value={date}
                onChange={setDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Shift</InputLabel>
                <Select value={shift} label="Shift" onChange={e => setShift(e.target.value)}>
                  <MenuItem value="DAY">DAY</MenuItem>
                  <MenuItem value="NIGHT">NIGHT</MenuItem>
                  <MenuItem value="BOTH">BOTH (DAY + NIGHT)</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                onClick={fetchData}
                disabled={loading}
                sx={{
                  minWidth: 140,
                  backgroundColor: INDIGO,
                  '&:hover': { backgroundColor: '#1a237e' },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {loading ? 'Loading…' : 'Search'}
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {loading && (
        <Box textAlign="center" py={6}>
          <CircularProgress sx={{ color: INDIGO }} />
          <Typography sx={{ mt: 2, color: '#888' }}>Fetching data…</Typography>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Results card ─────────────────────────────────────────────────────── */}
      {data && (
        <Card sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #c5cae9',
        }}>
          <Box sx={{
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 2, pt: 1,
          }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { fontWeight: 600, fontSize: '0.78rem' },
                '& .Mui-selected': { color: INDIGO },
                '& .MuiTabs-indicator': { backgroundColor: INDIGO },
              }}
            >
              {TAB_LABELS.map((l, i) => <Tab key={i} label={l} />)}
            </Tabs>
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{ color: INDIGO, fontWeight: 600, textTransform: 'none', ml: 1, flexShrink: 0 }}
            >
              Excel
            </Button>
          </Box>

          {tab === 0 && <DetailTable  rows={data[0]} />}
          {tab === 1 && <SummaryTable rows={data[1]} />}
          {tab === 2 && <SummaryTable rows={data[2]} />}
          {tab === 3 && <SummaryTable rows={data[3]} />}
          {tab === 4 && <SummaryTable rows={data[4]} />}
        </Card>
      )}
    </Container>
  );
};

export default DailyCoreComposerReportView;
